import { useState } from 'react'
import { api } from '../../api/endpoints'
import type { AccessReason, ManagedInfo } from '../../api/types'
import { Icon } from '../../components/Icon'
import { unsubscribeAdminChannel } from '../../realtime/subscriptions'
import { toast } from '../../state/toasts'
import { syncAll } from '../../sync/syncEngine'
import { toastChannelError } from '../channels/channelErrors'
import { useEphemeral } from '../../state/ephemeral'

const REASON_LABELS: Record<AccessReason, string> = {
  business: 'Business',
  hr: 'HR',
  legal: 'Legal',
  safeguarding: 'Safeguarding',
  security: 'Security',
}
const reasonLabel = (reason: AccessReason) => REASON_LABELS[reason] ?? reason
const untilLabel = (until: string) => new Date(until).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })

export function AdminBanner({ channelId, managed = null, joinable = true }: { channelId: string; managed?: ManagedInfo | null; joinable?: boolean }) {
  const [busy, setBusy] = useState(false)
  const access = useEphemeral((s) => s.adminAccess[channelId])
  // Only the integration adds people to a managed_only channel, admins included.
  const joinBlocked = managed?.policy === 'managed_only'

  const join = async () => {
    setBusy(true)
    try {
      await api.join(channelId)
      // Members are joined to the hub group by the server; the explicit subscription is no longer needed.
      unsubscribeAdminChannel(channelId)
      await syncAll()
      toast('You joined the channel')
    } catch (error) {
      toastChannelError("Couldn't join.", error)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="admin-banner" role="note">
      <Icon name="eye" />
      <span>
        You are viewing as an admin
        {/* What was written down when this was opened, so it is in front of whoever is reading. */}
        {access ? ` · ${reasonLabel(access.reason)} · logged until ${untilLabel(access.until)}` : ''}
        {joinBlocked ? '. Membership is managed automatically.' : ''}
      </span>
      {/* Admins can join channels, never someone else's direct or bot conversation. */}
      {joinBlocked || !joinable ? null : (
        <button type="button" className="button primary small" disabled={busy} onClick={() => void join()}>
          Join
        </button>
      )}
    </div>
  )
}
